import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';
import { OAuthButton } from './OAuthButton';
import GlobalNavBar from '../layout/GlobalNavBar';
import { trackLogin } from '../../utils/analytics';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const login = useAuthStore((state) => state.login);
  const { t } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setOauthError(null);
    try {
      await login(data.email, data.password);
      // GA4 — login success. The userType is unknown at this exact
      // point (auth store updates async); leave `user_role` off and
      // let `AnalyticsUserContext` send it on the next push.
      trackLogin({ method: 'email' });
      // Post-login redirect is owned by <PublicRoute> in App.tsx, which
      // reads the LocationTracker's last-visited path (or the stored
      // redirect intent). Doing a second `navigate()` here races with
      // that guard and usually loses, sending the user to /dashboard.
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthError = (error: Error) => {
    console.error('OAuth error:', error);
    setOauthError(error.message);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />

      {/* Content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:signInToAccount')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth:or')}{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth:createNewAccount')}
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                {t('auth:emailAddress')}
              </label>
              <input
                {...register('email', {
                  required: t('auth:errors.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth:errors.invalidEmail'),
                  },
                })}
                type="email"
                autoComplete="email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t('auth:emailAddress')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                {t('auth:password')}
              </label>
              <input
                {...register('password', {
                  required: t('auth:errors.passwordRequired'),
                })}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t('auth:password')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('auth:forgotPassword')}
              </Link>
            </div>
            <div className="text-sm">
              <Link
                to="/confirm-email"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('auth:confirmYourEmail')}
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth:signingIn') : t('auth:signIn')}
            </button>
          </div>
        </form>

        {/* OAuth Error Display */}
        {oauthError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{oauthError}</p>
          </div>
        )}

        {/* Visual Separator */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">
                {t('auth:or')}
              </span>
            </div>
          </div>
        </div>

        {/* OAuth Buttons */}
        <div className="mt-6 space-y-3">
          <OAuthButton
            provider="google"
            mode="signin"
            onError={handleOAuthError}
          />
          <OAuthButton
            provider="naver"
            mode="signin"
            onError={handleOAuthError}
          />
          <OAuthButton
            provider="kakao"
            mode="signin"
            onError={handleOAuthError}
          />
        </div>
        </div>
      </div>
    </div>
  );
};
